import type { ComponentChildren } from "preact";
import { Dialog as SheetPrimitive } from "@base-ui/react/dialog";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { usePortalContainer } from "@/context/PortalContext";
import { X } from "lucide-react";

function Sheet({ ...props }: SheetPrimitive.Root.Props) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: SheetPrimitive.Trigger.Props) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: SheetPrimitive.Close.Props) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: SheetPrimitive.Portal.Props) {
  const container = usePortalContainer();
  return (
    <SheetPrimitive.Portal
      data-slot="sheet-portal"
      container={container}
      {...props}
    />
  );
}

function SheetOverlay({
  className,
  onOpenChange,
  ...props
}: SheetPrimitive.Backdrop.Props & { onOpenChange?: (open: boolean) => void }) {
  return (
    <SheetPrimitive.Backdrop
      data-slot="sheet-overlay"
      onClick={() => onOpenChange?.(false)}
      className={cn(
        "tw:data-open:animate-in tw:data-closed:animate-out tw:data-closed:fade-out-0 tw:data-open:fade-in-0 tw:bg-black/20 tw:duration-100 tw:data-ending-style:opacity-0 tw:data-starting-style:opacity-0 tw:supports-backdrop-filter:backdrop-blur-xs tw:fixed tw:inset-0 tw:z-[9999]",
        className
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  onOpenChange,
  ...props
}: SheetPrimitive.Popup.Props & {
  side?: "top" | "right" | "bottom" | "left";
  showCloseButton?: boolean;
  children: ComponentChildren;
  onOpenChange?: (open: boolean) => void;
}) {
  return (
    <SheetPortal>
      <SheetOverlay onOpenChange={onOpenChange} />
      <SheetPrimitive.Popup
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "tw:bg-background tw:data-open:animate-in tw:data-closed:animate-out tw:data-[side=right]:data-closed:slide-out-to-right-10 tw:data-[side=right]:data-open:slide-in-from-right-10 tw:data-[side=left]:data-closed:slide-out-to-left-10 tw:data-[side=left]:data-open:slide-in-from-left-10 tw:data-[side=top]:data-closed:slide-out-to-top-10 tw:data-[side=top]:data-open:slide-in-from-top-10 tw:data-closed:fade-out-0 tw:data-open:fade-in-0 tw:data-[side=bottom]:data-closed:slide-out-to-bottom-10 tw:data-[side=bottom]:data-open:slide-in-from-bottom-10 tw:fixed tw:z-[9999] tw:flex tw:flex-col tw:gap-4 tw:bg-clip-padding tw:text-sm tw:shadow-lg tw:transition tw:duration-200 tw:ease-in-out tw:data-[side=bottom]:inset-x-0 tw:data-[side=bottom]:bottom-0 tw:data-[side=bottom]:h-auto tw:data-[side=left]:inset-y-0 tw:data-[side=left]:left-0 tw:data-[side=left]:h-full tw:data-[side=left]:w-3/4 tw:data-[side=left]:border-r tw:data-[side=right]:inset-y-0 tw:data-[side=right]:right-0 tw:data-[side=right]:h-full tw:data-[side=right]:w-3/4 tw:data-[side=right]:border-l tw:data-[side=top]:inset-x-0 tw:data-[side=top]:top-0 tw:data-[side=top]:h-auto tw:data-[side=top]:border-b tw:data-[side=left]:sm:max-w-sm tw:data-[side=right]:sm:max-w-sm",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close
            data-slot="sheet-close"
            render={
              <Button
                variant="ghost"
                className="tw:absolute tw:top-4 tw:right-4 tw:focus-visible:border-transparent tw:focus-visible:ring-0"
                size="icon-sm"
              />
            }
          >
            <X />
            <span className="tw:sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Popup>
    </SheetPortal>
  );
}

function SheetHeader({
  className,
  children,
  ...props
}: {
  className?: string;
  children: ComponentChildren;
}) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("tw:gap-1.5 tw:p-4 tw:flex tw:flex-col", className)}
      {...props}
    >
      {children}
    </div>
  );
}

function SheetFooter({
  className,
  children,
  ...props
}: {
  className?: string;
  children: ComponentChildren;
}) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        "tw:gap-2 tw:p-4 tw:mt-auto tw:flex tw:flex-col",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function SheetTitle({
  className,
  children,
  ...props
}: SheetPrimitive.Title.Props & {
  className?: string;
  children: ComponentChildren;
}) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("tw:text-foreground tw:font-medium", className)}
      {...props}
    >
      {children}
    </SheetPrimitive.Title>
  );
}

function SheetDescription({
  className,
  children,
  ...props
}: SheetPrimitive.Description.Props & {
  className?: string;
  children: ComponentChildren;
}) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("tw:text-muted-foreground tw:text-sm", className)}
      {...props}
    >
      {children}
    </SheetPrimitive.Description>
  );
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
